import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import { generateDataTestId } from '../../../../utils';
import { useMetadata, Lookup } from '../../../../contexts/MetadataContext';
import { PropertyField } from '../../../../system/field';
import { ListingResource, useListingField } from '@brightmls/nextgen.lae.flc';

interface SubPropertyTypeSelectProps {
  propertyType: string;
}

const SubPropertyTypeSelect: React.FC<SubPropertyTypeSelectProps> = ({
  propertyType,
}) => {
  const { relatedLookups } = useMetadata();
  const subPropertyTypeFromListing = useListingField<string>(
    ListingResource.Property,
    PropertyField.PROPERTY_SUB_TYPE,
    '',
  );

  const subPropertyTypes: Lookup[] = useMemo(() => {
    const relatedLookupValues = relatedLookups(
      'PropertySubType',
      'PropertyType',
      propertyType,
    );
    if (!relatedLookupValues) {
      return [];
    }
    return relatedLookupValues;
  }, [propertyType, relatedLookups]);

  const [subPropertyType, setSubPropertyType] = useState(
    subPropertyTypes?.find(
      (lookup) => lookup.LookupValue === subPropertyTypeFromListing.value,
    ) || null,
  );

  // if Property type changes, clear out value for SubProperty Type
  useEffect(() => {
    setSubPropertyType(null);
    subPropertyTypeFromListing.updateValue('');
  }, [propertyType]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: '700',
          display: 'flex',
          alignItems: 'flex-start',
        }}
        variant="subtitle1"
        className="bmls_typography"
        component="label"
      >
        Sub Property Type{' '}
        <span
          style={{
            color: 'red',
            fontSize: '28px',
            lineHeight: '28px',
            height: '24px', // this line prevents the taller 28px height from increasing the spacing between the label and form field
            marginLeft: '4px',
          }}
        >
          *
        </span>
      </Typography>

      <FormControl fullWidth margin="normal">
        <Autocomplete
          data-testid={generateDataTestId(
            'Sub Property Type',
            undefined,
            'selectField',
          )}
          options={subPropertyTypes}
          isOptionEqualToValue={(option, value) => {
            return option.DisplayValue === value.DisplayValue;
          }}
          value={subPropertyType}
          getOptionLabel={(option) => option?.DisplayValue ?? ''}
          onChange={(_event, newValue) => {
            setSubPropertyType(newValue);
            if (newValue === null) {
              subPropertyTypeFromListing.updateValue('');
            }
            if (newValue?.LookupValue) {
              subPropertyTypeFromListing.updateValue(newValue?.LookupValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder="Select Sub Property Type"
              variant="outlined"
            />
          )}
        />
      </FormControl>
    </Box>
  );
};

export default SubPropertyTypeSelect;
